import { datadogRum } from '@datadog/browser-rum'
import { getCurrentScope } from '@sentry/browser'

import {
    SegmentEventName,
    type SegmentEventNameValue,
} from 'const/monitoring/segment'
import { registerFullstoryUser } from 'features/Monitoring/FullStory'
import { identifyUserInPlanHat } from 'features/Monitoring/PlanHat'
import {
    getIdentifyProps,
    sendSegmentTrackAndIdentifyEvents,
    sendTrackEvent as sendSegmentTrackEvent,
} from 'features/Monitoring/Segment/service'
import { extractDomainFromEmail } from 'helpers/email'
import { Organization, OrganizationGroup, User } from 'types'

interface ContextParams {
    id: string
    email: string
    organizationId?: string
    organizationName?: string
    organizationGroupId?: string
    organizationGroupName?: string
}

const setSentryUserContext = ({
    id,
    email,
    organizationId,
    organizationName,
    organizationGroupId,
    organizationGroupName,
}: ContextParams): void => {
    const scope = getCurrentScope()
    scope.setTags({
        organizationId,
        organizationName,
        organizationGroupId,
        organizationGroupName,
    })
    scope.setUser({
        id,
        email,
    })
}

const setDatadogUserContext = ({
    id,
    email,
    organizationId,
    organizationName,
    organizationGroupId,
    organizationGroupName,
}: ContextParams): void => {
    // https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#user-session
    datadogRum.setUser({
        id,
        email,
        organizationId,
        organizationName,
        organizationGroupId,
        organizationGroupName,
        ...extractDomainFromEmail(email),
    })
}

const unsetSentryUserContext = (): void => {
    const scope = getCurrentScope()

    scope.setUser(null)
    scope.clear()
}

const unsetDatadogUserContext = (): void => {
    datadogRum.clearUser()
}

export function setDebugUserContext(
    id: string,
    email: string,
    organization: Organization | null,
    organizationGroup: OrganizationGroup | null
): void {
    const context = {
        id,
        email,
        organizationId: organization?.id,
        organizationName: organization?.name,
        organizationGroupId: organizationGroup?.id,
        organizationGroupName: organizationGroup?.name,
    }

    setSentryUserContext(context)
    setDatadogUserContext(context)
}

export function unsetDebugUserContext(): void {
    unsetSentryUserContext()
    unsetDatadogUserContext()
}

export async function setThirdPartyTracking(
    currentUser: User,
    organization: Organization | null,
    gilId?: string
): Promise<void> {
    const salesforceId = organization?.salesforce_id

    await Promise.all([
        registerFullstoryUser(currentUser),
        sendSegmentTrackAndIdentifyEvents(
            SegmentEventName.SIGNED_IN,
            {},
            gilId ?? '',
            currentUser.id,
            getIdentifyProps(currentUser, gilId, salesforceId)
        ),
        identifyUserInPlanHat(salesforceId, gilId),
    ])
}

export function sendTrackEvent(eventName: SegmentEventNameValue): void {
    sendSegmentTrackEvent(eventName, {})
}
